import { Button } from "@mui/material";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useAccessControl, useUrlParams } from "@/hooks";
import { Locked, Unlocked } from "@/icons";
import { ExperienceService } from "@/services";
import { updateCurrentExperience } from "@/store/actions";
import { currentExperienceSelector } from "@/store/selectors";

const svgLockStyle = { width: "18", height: "18" };

export default function ExperienceLockingButton() {
  const dispatch = useDispatch();
  const params = useUrlParams();
  const { canAccess } = useAccessControl();
  const currExperience = useSelector(currentExperienceSelector);

  const { icon: Icon, title } = useMemo(
    () => ({
      icon: currExperience.experience.locked ? Locked : Unlocked,
      title: currExperience.experience.locked ? "Locked" : "Unlocked",
    }),
    [currExperience.experience.locked],
  );

  const handleClickToLock = async (e) => {
    e.stopPropagation();

    const newCurrExperience = structuredClone(currExperience);
    newCurrExperience.experience.locked = !newCurrExperience.experience.locked;

    await ExperienceService.updateExperience(params.propertyId, currExperience.experience.id, {
      locked: newCurrExperience.experience.locked,
    });

    dispatch(updateCurrentExperience(newCurrExperience));
  };

  return (
    <Button
      disableRipple
      disabled={!canAccess({ allowedPermissions: ["experiences_lock:create"] })}
      variant="text"
      color="secondary"
      startIcon={<Icon {...svgLockStyle} />}
      onClick={handleClickToLock}
      sx={{
        "&.Mui-disabled": {
          cursor: "not-allowed",
          pointerEvents: "auto",
          color: "white",
          opacity: 0.5,
        },
      }}
      data-testid="experience-locking-button"
      data-analytics={`vb-experience-${currExperience.experience.locked ? "unlock" : "lock"}`}
    >
      {title}
    </Button>
  );
}
